<script>
import { required, email, minLength, sameAs } from "vuelidate/lib/validators";

import { renderizarBotonGoogle } from "@/services/googleIdentity";
import { registrarConCorreo, registrarConGoogle } from "@/services/registro";
import { TERMINOS } from "@/services/documentos";

/**
 * Alta de cuentas nuevas desde el portal.
 *
 * Crea la misma estructura que la app (Auth + doc en `app_usuarios`), así que
 * quien se registra aquí puede entrar también desde el celular. Es el
 * complemento de `portal/login.vue`, que sólo deja entrar a cuentas ya creadas.
 */
export default {
  page: { title: "Crear cuenta en Recnet" },
  data() {
    return {
      email: "",
      password: "",
      confirmar: "",
      acepta: false,
      terminos: TERMINOS,
      enviado: false,
      registrando: false,
      registrandoGoogle: false,
      googleError: null,
      error: null,
    };
  },
  validations: {
    email: { required, email },
    // Firebase rechaza contraseñas de menos de 6 caracteres; validarlo aquí
    // evita un error crudo de Firebase.
    password: { required, minLength: minLength(6) },
    confirmar: { required, sameAsPassword: sameAs("password") },
    acepta: { debeAceptar: (v) => v === true },
  },
  methods: {
    async registrar() {
      this.enviado = true;
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.registrando = true;
      this.error = null;
      try {
        // Todos se registran como usuario; si tienen negocio lo registran
        // después desde Network, que los activa como comercio.
        await registrarConCorreo({
          email: this.email,
          password: this.password,
          tipo: "normal",
        });
        await this.entrar();
      } catch (err) {
        this.error = this.mensajeDeError(err);
      } finally {
        this.registrando = false;
      }
    },

    /** Llega el token del botón de Google. */
    async registrarGoogle(idToken) {
      if (!this.acepta) {
        this.googleError = "Primero acepta los términos y condiciones.";
        return;
      }
      this.registrandoGoogle = true;
      this.googleError = null;
      try {
        await registrarConGoogle({ idToken, tipo: "normal" });
        await this.entrar();
      } catch (err) {
        this.googleError = this.mensajeDeError(err);
      } finally {
        this.registrandoGoogle = false;
      }
    },

    /** Tras crear la cuenta ya hay sesión: se carga el perfil y se entra. */
    async entrar() {
      // Sin esto el estado de auth queda en null (el registro no pasa por la
      // acción `logIn`) y el guard rebota al recién registrado al login.
      await this.$store.dispatch("auth/establecerUsuario");
      await this.$store.dispatch("cliente/cargar", { force: true });
      // El recién registrado va directo a Network para completar su registro,
      // no a Home (que además tiene el menú oculto hasta que complete).
      this.$router.push({ name: "portal-cuenta" });
    },

    async prepararBotonGoogle() {
      try {
        await renderizarBotonGoogle(this.$refs.botonGoogle, this.registrarGoogle);
      } catch (err) {
        console.error("[registro] no se pudo preparar el registro con Google:", err);
        this.googleError = "El registro con Google no está disponible en este momento.";
      }
    },

    mensajeDeError(err) {
      console.error("[registro] fallo al crear la cuenta:", err);
      switch (err && err.code) {
        case "auth/email-already-in-use":
          return "Ya existe una cuenta con este correo. Inicia sesión en su lugar.";
        case "auth/invalid-email":
          return "El correo no es válido.";
        case "auth/weak-password":
          return "La contraseña es muy débil. Usa al menos 6 caracteres.";
        case "auth/network-request-failed":
          return "No hay conexión. Revisa tu internet e inténtalo de nuevo.";
        case "auth/unauthorized-domain":
          return "Este sitio no está autorizado para registrarse con Google. Avísale al equipo de Recnet.";
        default:
          return typeof err === "string"
            ? err
            : "No pudimos crear tu cuenta. Inténtalo de nuevo.";
      }
    },
  },
  mounted() {
    this.prepararBotonGoogle();
  },
};
</script>

<template>
  <div class="portal-registro">
    <div class="portal-registro-card">
      <div class="text-center mb-4">
        <img :src="require('@/assets/images/RCCN-LOGO.png')" alt="Recnet" class="portal-registro-logo" />
        <h1 class="h4 mt-3 mb-1">Crea tu cuenta</h1>
        <p class="text-muted mb-0">La misma cuenta te sirve en la web y en la app.</p>
      </div>

      <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>

      <b-form @submit.prevent="registrar">
        <b-form-group label="Correo electrónico">
          <b-form-input v-model="email" type="email" placeholder="tucorreo@ejemplo.com"
            autocomplete="email" :class="{ 'is-invalid': enviado && $v.email.$error }" />
          <div v-if="enviado && !$v.email.required" class="invalid-feedback d-block">Escribe tu correo.</div>
          <div v-else-if="enviado && !$v.email.email" class="invalid-feedback d-block">El correo no es válido.</div>
        </b-form-group>

        <b-form-group label="Contraseña">
          <b-form-input v-model="password" type="password" placeholder="Al menos 6 caracteres"
            autocomplete="new-password" :class="{ 'is-invalid': enviado && $v.password.$error }" />
          <div v-if="enviado && !$v.password.required" class="invalid-feedback d-block">Escribe una contraseña.</div>
          <div v-else-if="enviado && !$v.password.minLength" class="invalid-feedback d-block">
            Usa al menos 6 caracteres.
          </div>
        </b-form-group>

        <b-form-group label="Confirmar contraseña">
          <b-form-input v-model="confirmar" type="password" placeholder="Repite tu contraseña"
            autocomplete="new-password" :class="{ 'is-invalid': enviado && $v.confirmar.$error }" />
          <div v-if="enviado && $v.confirmar.$error" class="invalid-feedback d-block">
            Las contraseñas no coinciden.
          </div>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox v-model="acepta" :class="{ 'is-invalid': enviado && $v.acepta.$error }">
            Acepto los términos y condiciones
          </b-form-checkbox>
          <div class="terminos-enlaces">
            <a v-for="t in terminos" :key="t.subtitulo" :href="t.url" target="_blank" rel="noopener noreferrer"
              class="small text-muted mr-3">{{ t.subtitulo }}</a>
          </div>
          <div v-if="enviado && $v.acepta.$error" class="invalid-feedback d-block">
            Debes aceptar los términos para continuar.
          </div>
        </b-form-group>

        <b-button type="submit" variant="primary" block :disabled="registrando || registrandoGoogle">
          <b-spinner v-if="registrando" small class="mr-2" />
          {{ registrando ? 'Creando tu cuenta…' : 'Crear cuenta' }}
        </b-button>
      </b-form>

      <div class="separador"><span>o</span></div>

      <div ref="botonGoogle" class="contenedor-google"></div>
      <p v-if="registrandoGoogle" class="text-center text-muted small mt-2 mb-0">
        <b-spinner small class="mr-1" /> Registrando con Google…
      </p>
      <b-alert :show="!!googleError" variant="secondary" class="mt-2 mb-0 py-2 small">{{ googleError }}</b-alert>

      <p class="text-center text-muted small mt-4 mb-0">
        ¿Ya tienes cuenta?
        <router-link :to="{ name: 'portal-login' }">Inicia sesión</router-link>
      </p>
    </div>
  </div>
</template>

<style scoped>
.portal-registro {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: linear-gradient(135deg, #4b2e83 0%, #7b3fa0 100%);
}

.portal-registro-card {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.portal-registro-logo {
  height: 56px;
  width: auto;
}

.terminos-enlaces {
  margin-top: 4px;
}

.separador {
  display: flex;
  align-items: center;
  margin: 20px 0;
  color: #adb5bd;
  font-size: 0.85rem;
}

.separador::before,
.separador::after {
  content: "";
  flex: 1;
  border-top: 1px solid #e9ecef;
}

.separador span {
  padding: 0 12px;
}

.contenedor-google {
  display: flex;
  justify-content: center;
  min-height: 44px;
}
</style>
